import { fmtDateTime } from '../lib/dates.ts';
import { Modal } from './Modal';
import type { LiveState } from '../lib/live.ts';

/**
 * What a support call asks for, in one place.
 *
 * Reached by tapping the version line rather than living in a tab: a
 * three item bar on a 420px screen has no room for something looked
 * at once a year, and this is exactly where someone would look for
 * it anyway.
 */
const STATE: Record<LiveState, string> = {
  open: 'Connected',
  connecting: 'Connecting',
  offline: 'Not connected',
};


/**
 * What the browser can tell us about the machine.
 *
 * Not much, deliberately: a web page cannot see the local IP address
 * or the Windows build. Those come from the shell, which is why this
 * lists what it has and says plainly what it does not.
 */
const platform = (): string => {
  const ua = navigator.userAgent;
  if (ua.includes('Windows NT 10.0')) return 'Windows 10 or 11';
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Android')) return 'Android';
  if (/iPhone|iPad/.test(ua)) return 'iOS';
  if (ua.includes('Mac OS X')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  return 'Unknown';
};

const browser = (): string => {
  const ua = navigator.userAgent;
  if (ua.includes('Edg/')) return 'Edge';
  if (ua.includes('Chrome/')) return 'Chrome or WebView2';
  if (ua.includes('Firefox/')) return 'Firefox';
  if (ua.includes('Safari/')) return 'Safari';
  return 'Unknown';
};

/** Installed as an app, or open in a browser tab. */
const installed = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches;

export const NodeInfo = ({
  deviceName, siteName, version, publicIp,
  tenantName, live, onClose,
}: {
  deviceName?: string | null;
  siteName?: string | null;
  version?: string | null;
  /** As the server sees us, which the browser cannot know. */
  publicIp?: string | null;
  tenantName: string;
  live: LiveState;
  onClose: () => void;
}) => (
  <Modal
    title="About this screen"
    subtitle="Quote these details when asking for help."
    onClose={onClose}
  >
    <dl className="defList nodeFacts">
      <dt>Node</dt><dd>{deviceName ?? 'not a counter screen'}</dd>
      <dt>Site</dt><dd>{siteName ?? '--'}</dd>
      <dt>Account</dt><dd>{tenantName}</dd>
      <dt>Address</dt><dd>{window.location.hostname}</dd>
      <dt>Connection</dt><dd>{STATE[live]}</dd>
      <dt>App version</dt>
      <dd>{version ?? 'running in a browser'}</dd>
      <dt>Public IP</dt><dd>{publicIp ?? '--'}</dd>
      <dt>Local IP</dt><dd>available in the desktop app</dd>
      <dt>System</dt><dd>{platform()}</dd>
      <dt>Runtime</dt>
      <dd>{browser()}{installed() ? ', installed' : ', in a tab'}</dd>
      <dt>Screen</dt>
      <dd>
        {window.screen.width}&times;{window.screen.height}
      </dd>
      <dt>Time zone</dt>
      <dd>{Intl.DateTimeFormat().resolvedOptions().timeZone}</dd>
      <dt>Local time</dt><dd>{fmtDateTime(new Date().toISOString())}</dd>
    </dl>
    {/* Reading fourteen values down a phone is how a support call
        gets the wrong ones. One tap puts the lot on the clipboard. */}
    <button
      onClick={() => {
        const dl = document.querySelector('.nodeFacts');
        if (dl) void navigator.clipboard
          .writeText(dl.textContent ?? '')
          .catch(() => undefined);
      }}
    >
      Copy these details
    </button>

    <p className="sub" style={{ marginTop: 14 }}>
      The app updates itself. If the version here is behind, leave the
      machine on and it will catch up on its own.
    </p>
  </Modal>
);
